import type { ProsjektRow } from "./data/types";

type SortKey = Exclude<keyof ProsjektRow, "url">;
type SortDirection = "asc" | "desc";

const cardNumberFormatter = new Intl.NumberFormat("nb-NO", {
    maximumFractionDigits: 10,
});

const tableNumberFormatter = new Intl.NumberFormat("nb-NO");

export function truncateToSignificantDigits(value: number, significantDigits: number): number {
    if (!Number.isFinite(value) || value === 0) {
        return value;
    }

    const absValue = Math.abs(value);

    if (absValue < 1) {
        return Math.trunc(value * 1_000) / 1_000;
    }

    const digitsBeforeDecimal = Math.floor(Math.log10(absValue)) + 1;

    if (digitsBeforeDecimal >= significantDigits) {
        const divisor = 10 ** (digitsBeforeDecimal - significantDigits);
        return Math.trunc(value / divisor) * divisor;
    }

    const multiplier = 10 ** (significantDigits - digitsBeforeDecimal);
    return Math.trunc(value * multiplier) / multiplier;
}

export function formatCardNumber(value: number): string {
    return cardNumberFormatter.format(truncateToSignificantDigits(value, 4));
}

function formatWithSignificantDigits(value: number, significantDigits: number): string {
    const truncated = truncateToSignificantDigits(value, significantDigits);

    if (truncated === 0) {
        return cardNumberFormatter.format(0);
    }

    const digitsBeforeDecimal = Math.floor(Math.log10(Math.abs(truncated))) + 1;
    const decimalPlaces = Math.max(0, significantDigits - digitsBeforeDecimal);

    return new Intl.NumberFormat("nb-NO", {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(truncated);
}

const belopUnits = [
    { threshold: 1_000_000_000, divisor: 1_000_000_000, label: "milliarder" },
    { threshold: 1_000_000, divisor: 1_000_000, label: "millioner" },
    { threshold: 1_000, divisor: 1_000, label: "tusen" },
];

export function formatBelop(valueInKroner: number): string {
    const unit = belopUnits.find(({ threshold }) => Math.abs(valueInKroner) >= threshold);
    const scaledValue = unit ? valueInKroner / unit.divisor : valueInKroner;
    const formattedValue = formatWithSignificantDigits(scaledValue, 4);

    return unit ? `${formattedValue} ${unit.label} kroner` : `${formattedValue} kroner`;
}

export function getYearFromTimestamp(timestamp: number | null): number | null {
    if (!timestamp) {
        return null;
    }

    return new Date(timestamp).getUTCFullYear();
}

export function formatVedtaksaar(timestamp: number | null): string {
    const year = getYearFromTimestamp(timestamp);

    if (year === null) {
        return "Ikke oppgitt";
    }

    return String(year);
}

export function normalizeSearchValue(value: string | number | null): string {
    return String(value).toLocaleLowerCase("nb").trim();
}

export function shouldShowOriginalSupportAmount(
    row: Pick<ProsjektRow, "stottebelop" | "stottebelopOpprinnelig">,
): boolean {
    return row.stottebelop === 0 && row.stottebelopOpprinnelig > 0 && row.stottebelopOpprinnelig !== row.stottebelop;
}

export function getCancelledCellTooltip(
    columnKey: "stottebelop" | "utslippsreduksjon" | "energiresultat",
    value: number,
): string {
    const formattedValue = tableNumberFormatter.format(value);

    if (columnKey === "stottebelop") {
        return `Prosjektet er kansellert. Opprinnelig støttebeløp var ${formattedValue} kr.`;
    }

    if (columnKey === "utslippsreduksjon") {
        return `Prosjektet er kansellert. Opprinnelig utslippsreduksjon var ${formattedValue} kg CO₂e.`;
    }

    return `Prosjektet er kansellert. Opprinnelig energiresultat var ${formattedValue} kWh.`;
}

export function getDefaultSortDirectionForSortKey(sortKey: SortKey): SortDirection {
    return sortKey === "vedtaksdato" ? "desc" : "asc";
}
